import React, { useEffect, useState } from "react";
import AgGridWrapper from "../UI/AgGrid";
import PrimaryModal from "../UI/Modal/PrimaryModal";
import { Form, Formik } from "formik";
import { FormikInput, PrimaryButton, SecondaryButton } from "../UI";
import * as Yup from "yup";
import { useContentfulContext } from "../../contexts/ContentfulContext";
import ConfirmModal from "../UI/Modal/confirmModal";
import Loader from "../UI/Loader/Loader";

const UpdateOfferAssociationModal = ({
  isOpen,
  onClose,
  associatedExperiences = [],
  onSaveAsNewOffer,
  onUpdateAssociatedExperience,
  onDelinkExperience,
  loading
}) => {
  const { content } = useContentfulContext();
  const text = content.EDIT_OFFER_CONTENT;

  const OfferNameSchema = Yup.object().shape({
    offer_name: Yup.string().required(text.update_offer_association.errors.name_required),
  });

  const [activations, setActivations] = useState([]);
  const [showNewOfferModal, setShowNewOfferModal] = useState(false);
  const [showDelinkConfirmModal, setShowDelinkConfirmModal] = useState(false);

  const columnDefs = [
    { headerName: text.update_offer_association.experience_name, field: "name", flex: 1 },
    { headerName: text.update_offer_association.experience_id, field: "id", flex: 1 },
  ];

  useEffect(() => {
    if (!associatedExperiences || associatedExperiences.length === 0) {
      setActivations([]);
      return;
    }

    const formatted = associatedExperiences.map((exp) => ({
      id: exp.experience_id,
      name: exp.experience_name,
    }));

    setActivations(formatted);
  }, [associatedExperiences]);

  return (
    <PrimaryModal isOpen={isOpen} hideModal={onClose} title={text.update_offer_association.title} widthClass="min-w-max">
      { loading && <Loader/> }
      <div className="p-6 space-y-6">
        <p className="text-sm font-medium text-gray-700">
          {text.update_offer_association.configure_offer}
        </p>

        {activations.length > 0 ? (
          <div style={{ height: "280px", overflow: "auto", width: "100%" }}>
            <AgGridWrapper rowData={activations} columnDefs={columnDefs} domLayout="autoHeight" />
          </div>
        ) : (
          <p className="text-gray-500 text-sm">{text.update_offer_association.no_experiences || "No associated experiences found."}</p>
        )}

        <div className="flex justify-end gap-3 pt-4">
          <SecondaryButton handleClick={onClose}>{text.update_offer_association.cancel_btn}</SecondaryButton>

          <PrimaryButton handleClick={() => setShowNewOfferModal(true)}>
            {text.update_offer_association.dropdown_btn.save_experience.name}
          </PrimaryButton>

          <PrimaryButton
            handleClick={() => {
              if (onUpdateAssociatedExperience) onUpdateAssociatedExperience();
              onClose();
            }}
          >
            {text.update_offer_association.dropdown_btn.update_experience.name}
          </PrimaryButton>

          <PrimaryButton
            handleClick={() => setShowDelinkConfirmModal(true)}
          >
            {text.update_offer_association.dropdown_btn.delink_experience.name}
          </PrimaryButton>
        </div>
      </div>

      {showNewOfferModal && (
        <PrimaryModal
          isOpen={showNewOfferModal}
          hideModal={() => setShowNewOfferModal(false)}
          title={text.update_offer_association.dropdown_btn.save_experience.name}
          width="500px"
        >
          <div className="p-6 space-y-6">
            <Formik
              initialValues={{ offer_name: "" }}
              validationSchema={OfferNameSchema}
              onSubmit={(values) => {
                onSaveAsNewOffer(values.offer_name);
                setShowNewOfferModal(false);
                onClose();
              }}
            >
              {() => (
                <Form className="space-y-4 md:space-y-6">
                  <FormikInput
                    type="text"
                    id="offer_name"
                    name="offer_name"
                    label={text.offer_name}
                  />

                  <div className="flex items-center justify-end space-x-2">
                    <PrimaryButton type="submit">{text.update_offer_association.next_btn}</PrimaryButton>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </PrimaryModal>
      )}

      {showDelinkConfirmModal && (
        <ConfirmModal
          isOpen={showDelinkConfirmModal}
          hideModal={() => setShowDelinkConfirmModal(false)}
          title="Confirm Delink"
          description="Are you sure you want to delink this offer from all associated experiences? This also updates the offer."
          ConfirmText="Yes, Delink"
          CancelText="Cancel"
          loading={false}
          handleConfirmation={async () => {
            setShowDelinkConfirmModal(false);
            if (onDelinkExperience) await onDelinkExperience();
            onClose();
          }}
        />
      )}
    </PrimaryModal>
  );
};

export default UpdateOfferAssociationModal;
